import { useRef } from "react";
import Toran from "../art/Garland";
import { Latkan, Shehnai } from "../art/Instruments";
import { MarigoldDivider } from "../art/Icons";
import { Butterflies, Petals } from "../art/Ambience";
import Reveal from "../Reveal";
import useLive from "../useLive";

/*
 * The traditional touches a page can carry (`deco`, any combination):
 * - "toran":   a marigold toran across the top
 * - "latkans": latkans swinging from both top corners
 * - "shehnai": a crossed pair of shehnais above the heading
 * - "petals":  a few petals drifting down the page, behind its content
 * - "butterflies": a pair of butterflies wandering about the margins
 */
const Latkans = () => (
  <div className="ut-section__latkans" aria-hidden="true">
    <div className="ut-section__latkan-group ut-section__latkan-group--left">
      <Latkan color="#d81b60" bead="#f6b21b" />
      <Latkan className="ut-latkan--short" color="#f57c00" bead="#d81b60" />
    </div>
    <div className="ut-section__latkan-group ut-section__latkan-group--right">
      <Latkan className="ut-latkan--short" color="#00897b" bead="#f6b21b" />
      <Latkan color="#7b3fa0" bead="#f6b21b" />
    </div>
  </div>
);

/**
 * One page of the invitation. Every page shares the same header: a tracked kicker,
 * a title (or an italic lead), and the marigold divider. `tone` tints the page
 * (peach, blush or haldi). `top` is anything that stands above the header.
 */
const Section = ({ id, className = "", label, kicker, title, lead, top, deco = [], tone = "peach", seed = 50, children }) => {
  const ref = useRef(null);
  useLive(ref);
  const has = (d) => deco.includes(d);
  const decoClasses = deco.map((d) => `ut-section--${d}`).join(" ");
  return (
    <section ref={ref} id={id} className={`ut-section ut-section--${tone} ${decoClasses} ${className}`} aria-label={label}>
      {has("petals") && <Petals className="ut-section__petals" count={10} seed={seed} spread />}
      {has("toran") && <Toran className="ut-section__toran" />}
      {has("latkans") && <Latkans />}
      {has("butterflies") && <Butterflies className="ut-section__butterflies" seed={seed} />}
      <div className="ut-section__inner">
        {top}
        <Reveal className="ut-section__head">
          {has("shehnai") && (
            <div className="ut-section__shehnais" aria-hidden="true">
              <span className="ut-section__shehnai ut-section__shehnai--left">
                <Shehnai ribbon="#d81b60" />
              </span>
              <span className="ut-section__shehnai ut-section__shehnai--right">
                <Shehnai ribbon="#f57c00" />
              </span>
              <svg className="ut-section__shehnai-knot" viewBox="-12 -12 24 24">
                <use href="#ut-marigold" x="-11" y="-11" width="22" height="22" />
              </svg>
            </div>
          )}
          {kicker && <p className="ut-kicker">{kicker}</p>}
          {title && <h2 className="ut-title">{title}</h2>}
          {lead && <p className="ut-lead">{lead}</p>}
          <MarigoldDivider />
        </Reveal>
        {children}
      </div>
    </section>
  );
};

export default Section;
